/**
 * Consent Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ConsentUpdate } from '../../types/consent';

const ConsentEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<ConsentUpdate>({
    granted: true,
    withdrawn_at: null,
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchConsent = async () => {
      if (!id) {
        setError('No consent ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/consents/${id}`);
        const consent = response.data;

        setFormData({
          granted: consent.granted ?? true,
          withdrawn_at: consent.withdrawn_at || null,
          is_active: consent.is_active ?? true,
        });
      } catch (err: any) {
        console.error('Error fetching consent:', err);
        if (err.response?.status === 404) {
          setError('Consent not found');
        } else {
          setError('Failed to load consent data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchConsent();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: ConsentUpdate = {};

      if (formData.granted !== undefined) payload.granted = formData.granted;
      if (formData.withdrawn_at !== undefined) payload.withdrawn_at = formData.withdrawn_at;
      if (formData.is_active !== undefined) payload.is_active = formData.is_active;

      await axios.patch(`/api/consents/${id}`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/consents');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating consent:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update consent. Please check your input.');
        }
      } else {
        setError('Failed to update consent. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && formData.granted === undefined) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/consents')} sx={{ mt: 2 }}>
          Back to Consents
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/consents')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Consents
        </MuiLink>
        <Typography color="text.primary">Edit Consent</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Consent</Typography>
        <Typography variant="body2" color="textSecondary">Update the consent record</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Consent updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.granted ?? true} onChange={handleChange} name="granted" disabled={loading} />}
              label="Consent Granted"
            />

            <TextField
              fullWidth
              type="datetime-local"
              label="Withdrawn At"
              name="withdrawn_at"
              value={formData.withdrawn_at || ''}
              onChange={handleChange}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty if consent is still active"
            />

            <FormControlLabel
              control={<Checkbox checked={formData.is_active ?? true} onChange={handleChange} name="is_active" disabled={loading} />}
              label="Active"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/consents')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Consent'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default ConsentEditForm;
